import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Card,
    CardContent,
    IconButton,
    CircularProgress,
    Container,
    Divider,
    Fade,
    Link
} from '@mui/material';
import {
    Search as SearchIcon,
    CloudUpload as UploadIcon,
    Description as FileIcon,
    CheckCircle as SuccessIcon,
    ArrowBack as BackIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { getProviderById } from '../FunctionLogin';
import { uploadFile, createFolder, listFiles } from '../../services/docsService';
import swal from 'sweetalert';
import { useTranslation } from 'react-i18next';

export default function SSTUpload() {
    const [t] = useTranslation("global");
    const [providerId, setProviderId] = useState("");
    const [providerData, setProviderData] = useState(null);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const handleSearch = async () => {
        if (!providerId.trim()) return;
        setLoadingSearch(true);
        setProviderData(null);
        setUploadSuccess(false);
        try {
            const result = await getProviderById(providerId);
            if (result && result.length > 0) {
                const data = result[0];
                setProviderData(data);
                // Si buscó por NIT, actualizar el ID con el CBP_UUID real de SAP
                if (data.Proveedor && providerId !== data.Proveedor) {
                    setProviderId(data.Proveedor);
                }
            } else {
                swal(t("sst_content.no_encontrado"), t("sst_content.no_encontrado_desc"), "warning");
            }
        } catch (error) {
            console.error(error);
            swal("Error", t("sst_content.error_buscar"), "error");
        } finally {
            setLoadingSearch(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            const filesArray = Array.from(e.target.files);
            setSelectedFiles((prev) => [...prev, ...filesArray]);
            setUploadSuccess(false);
        }
    };

    const removeFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0 || !providerData) return;
        setUploading(true);
        try {
            const rootPath = providerData.Proveedor + "/";
            const sstPath = rootPath + "Documentos SST/";

            // Try to create the folder first
            try {
                await createFolder(sstPath);
            } catch (e) { }

            // Upload all files
            for (const file of selectedFiles) {
                await uploadFile(sstPath, file);
            }

            swal("Éxito", `${selectedFiles.length} ${t("sst_content.exito_msg")}`, "success");
            setUploadSuccess(true);
            setSelectedFiles([]);
        } catch (error) {
            console.error(error);
            swal("Error", t("sst_content.error_subida"), "error");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#f0f2f5',
            py: 6,
            backgroundImage: 'radial-gradient(circle at 50% -20%, #ffffff 0%, #f0f2f5 100%)'
        }}>
            <Container maxWidth="md">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a237e', mb: 2 }}>
                        {t("sst_content.titulo")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', maxWidth: 600, mx: 'auto' }}>
                        {t("sst_content.subtitulo")}
                    </Typography>
                </Box>

                <Paper elevation={4} sx={{ p: 4, borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                            {t("sst_content.paso1")}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label={t("sst_content.label_id")}
                                variant="outlined"
                                value={providerId}
                                onChange={(e) => setProviderId(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                placeholder="Ej: 2000000000"
                                sx={{ bgcolor: '#fff' }}
                            />
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSearch}
                                disabled={loadingSearch}
                                startIcon={loadingSearch ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                sx={{ px: 4, borderRadius: 2, bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' } }}
                            >
                                {t("sst_content.buscar")}
                            </Button>
                        </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        {t("sst_content.ayuda_id")}
                    </Typography>

                    {providerData && (
                        <Fade in={!!providerData}>
                            <Box>
                                <Divider sx={{ my: 4 }} />
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                                    {t("sst_content.paso2")}
                                </Typography>
                                <Card sx={{ bgcolor: '#e8eaf6', borderRadius: 3, border: '1px solid #c5cae9', mb: 4 }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                        <Box sx={{
                                            bgcolor: '#1a237e',
                                            color: '#fff',
                                            p: 2,
                                            borderRadius: 2,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <FileIcon fontSize="large" />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                                                {providerData.Tproveedor || t("sst_content.proveedor_sin_nombre")}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ID: {providerData.Proveedor} | NIT: {providerData.Nit}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {providerData.Ciudad}, {providerData.Pais}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                                    {t("sst_content.paso3")}
                                </Typography>

                                <Box sx={{
                                    border: '2px dashed #9fa8da',
                                    borderRadius: 4,
                                    p: 6,
                                    textAlign: 'center',
                                    bgcolor: uploadSuccess ? '#e8f5e9' : '#fafafa',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    '&:hover': { bgcolor: '#f5f5f5', borderColor: '#3f51b5' }
                                }}>
                                    <input
                                        type="file"
                                        id="file-upload-sst"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                        disabled={uploading}
                                    />

                                    {uploadSuccess ? (
                                        <Box>
                                            <SuccessIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                                            <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                                                {t("sst_content.exito_titulo")}
                                            </Typography>
                                            <Button
                                                variant="text"
                                                onClick={() => setUploadSuccess(false)}
                                                sx={{ mt: 1 }}
                                            >
                                                {t("sst_content.subir_otro")}
                                            </Button>
                                        </Box>
                                    ) : (
                                        <label htmlFor="file-upload-sst" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                                            <UploadIcon sx={{ fontSize: 60, color: '#3f51b5', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                                {selectedFiles.length > 0
                                                    ? t("sst_content.seleccionados", { count: selectedFiles.length })
                                                    : t("sst_content.seleccionar")}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("sst_content.formatos")}
                                            </Typography>
                                        </label>
                                    )}

                                    {selectedFiles.length > 0 && !uploadSuccess && (
                                        <Box sx={{ mt: 3, textAlign: 'left' }}>
                                            <Divider sx={{ mb: 2 }} />
                                            {selectedFiles.map((file, idx) => (
                                                <Box key={idx} sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 1,
                                                    mb: 1,
                                                    bgcolor: '#fff',
                                                    borderRadius: 1,
                                                    border: '1px solid #eee'
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <FileIcon color="primary" fontSize="small" />
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {file.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ({(file.size / 1024).toFixed(1)} KB)
                                                        </Typography>
                                                    </Box>
                                                    <IconButton size="small" onClick={() => removeFile(idx)} disabled={uploading}>
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        disabled={selectedFiles.length === 0 || uploading || uploadSuccess}
                                        onClick={handleUpload}
                                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                                        sx={{
                                            px: 6,
                                            py: 1.5,
                                            borderRadius: '30px',
                                            bgcolor: '#43a047',
                                            '&:hover': { bgcolor: '#2e7d32' },
                                            boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {uploading ? t("sst_content.subiendo") : t("sst_content.confirmar")}
                                    </Button>
                                </Box>
                            </Box>
                        </Fade>
                    )}
                </Paper>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t("sst_content.necesita_ayuda")} <Link href="/proveedores/Cerrar" underline="hover" sx={{ color: '#1a237e', fontWeight: 600 }}>{t("sst_content.portal_proveedores")}</Link>
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
